<div class="flex border-b-2 border-primary">
  <div class="flex-1 p-2">
    <h1 class="mb-2 text-2xl font-bold tracking-tight text-gray-900 text-center">All Users</h1>
    <p class="font-normal text-gray-700 text-center">Search by user to manage roles, branches, and locations</p>
  </div>

  @if (selectedUser(); as user) {
    <div class="flex-1 flex items-center pl-2 italic">{{ user.name }} ({{ user.email }})</div>
  }
</div>

<div class="flex h-full">
  <div class="flex-1 flex flex-col h-[600px] border-r-2 border-b-2 border-primary">
    <div class="flex mx-[-1px] items-center relative">
      <input
        panInput
        color="primary"
        class="w-full !rounded-none mt-[-1px]"
        placeholder="Filter {{ totalUsers() }} users..."
        [(ngModel)]="userFilter"
        (ngModelChange)="searchInput.next($event)"
      />

      <app-pan-button
        class="absolute right-[10px]"
        size="sm"
        color="info"
        (click)="newUserModal.show()"
        panRole
        [roles]="['admin', 'branch-manager', 'branch-owner']"
        >Add User</app-pan-button
      >
    </div>

    <div class="flex-1 overflow-hidden overflow-y-auto">
      @if (users().length === 0 && !hasLoadedPage()) {
        <app-pan-blank-slate> <app-pan-spinner></app-pan-spinner> &nbsp;Loading... </app-pan-blank-slate>
      } @else if (visibleUsers().length === 0 && hasLoadedPage()) {
        <app-pan-blank-slate>
          No users match your query.

          <app-pan-button
            icon="add"
            color="primary"
            fill="outline"
            (click)="newUserModal.show()"
            panRole
            [roles]="['admin', 'branch-manager', 'branch-owner']"
            >Add one</app-pan-button
          >
        </app-pan-blank-slate>
      } @else {
        <cdk-virtual-scroll-viewport #userScroll itemSize="50" class="h-full" data-testid="users-user-list">
          <div
            *cdkVirtualFor="let item of visibleUsers()"
            class="h-[50px] p-3 flex items-center border-b-2 cursor-pointer"
            [class.bg-primary-no-hover]="item.user_id === selectedUser()?.user_id"
            (click)="selectUser(item)"
          >
            <div class="flex flex-col">
              <div>{{ item.name }}</div>
              <div class="italic text-sm">{{ item.email }}</div>
            </div>
          </div>

          @if (isLoadingMoreUsers()) {
            <div class="h-[50px] p-3 flex items-center">
              <app-pan-spinner></app-pan-spinner>
            </div>
          }
        </cdk-virtual-scroll-viewport>
      }
    </div>
  </div>

  <div class="flex-1 h-[600px] overflow-hidden border-r-2 border-b-2 border-primary">
    @if (!hasLoadedLocations() || !hasLoadedBranches()) {
      <app-pan-blank-slate> <app-pan-spinner></app-pan-spinner> &nbsp;Loading locations & branches... </app-pan-blank-slate>
    } @else {
      @if (!selectedUser()) {
        <app-pan-blank-slate>Select a user to see branches and locations for it.</app-pan-blank-slate>
      } @else {
        <div class="p-2 flex justify-between" panRole [roles]="['admin']">
          <h1 class="text-lg font-bold">System Roles</h1>

          <div class="flex gap-2">
            @for (role of possibleSystemRoles; track $index) {
              <app-pan-button
                [attr.data-testid]="'system-role-' + role.id + '-button'"
                [attr.data-active]="hasSystemRole(role.id)"
                size="sm"
                [color]="hasSystemRole(role.id) ? 'info' : 'default'"
                (click)="toggleSystemRole(role.id)"
                [disabled]="amIEditingMyself() && role.id === 'admin'"
              >
                {{ role.name }}
              </app-pan-button>
            }
          </div>
        </div>

        <hr />

        <div class="p-2 flex justify-between">
          <div class="flex-1">
            <app-pan-button-group [(panRadioGroup)]="branchesOrLocations">
              <app-pan-button [panRadio]="'branches'" color="primary"> Branches ({{ visibleUserBranches().length }})</app-pan-button>
              <app-pan-button [panRadio]="'locations'" color="primary"> Locations ({{ visibleUserLocations().length }}) </app-pan-button>
            </app-pan-button-group>
          </div>

          <div class="flex-1 flex justify-end gap-2">
            <input type="file" class="hidden" #fileInput (change)="uploadPermissions($event)" accept=".json" />

            @switch (branchesOrLocations()) {
              @case ("locations") {
                <app-pan-button data-testid="add-location-button" color="info" (click)="addLocationModal.show()"> Add Location </app-pan-button>
              }
              @case ("branches") {
                <app-pan-button data-testid="add-branch-button" color="info" (click)="addBranchModal.show()"> Add Branch </app-pan-button>
              }
            }

            <app-pan-button
              icon="upload"
              color="warning"
              (click)="fileInput.click()"
              matTooltip="Upload Permissions"
              panRole
              [roles]="['admin']"
            >
            </app-pan-button>

            <app-pan-button
              icon="download"
              color="warning"
              (click)="downloadPermissions()"
              matTooltip="Download Permissions"
              panRole
              [roles]="['admin']"
            >
            </app-pan-button>
          </div>
        </div>

        <hr />

        <div class="h-full">
          @switch (branchesOrLocations()) {
            @case ("branches") {
              @if (visibleUserBranches().length === 0) {
                <app-pan-blank-slate>
                  There are no branches assigned to this user.

                  <app-pan-button icon="add" color="primary" fill="outline" (click)="addBranchModal.show()">Add one</app-pan-button>
                </app-pan-blank-slate>
              } @else {
                <cdk-virtual-scroll-viewport [itemSize]="50" class="h-[500px]">
                  <div *cdkVirtualFor="let item of visibleUserBranches()" class="h-[50px] p-2 flex items-center border-b-2 flex-row">
                    @let user = selectedUser();

                    <div class="flex-1 flex flex-col">
                      <div>{{ item.name }}</div>
                    </div>

                    <div class="flex-1 mr-2">
                      <ng-select
                        appendTo="body"
                        placeholder="Select roles..."
                        [bindValue]="'id'"
                        [bindLabel]="'name'"
                        [virtualScroll]="true"
                        [items]="item._selectableRoles"
                        [(ngModel)]="user.roles.branch[item.id]"
                        [multiple]="true"
                        [clearable]="false"
                        (add)="addUserBranchRole(user, $event, item)"
                        (remove)="removeUserBranchRole(user, $event, item)"
                      >
                        <ng-template ng-label-tmp let-role="item" let-clear="clear">
                          <span class="ng-value-label">{{ role.name }}</span>

                          @if (userLogin.canGrantBranchPermission(role.id, item.id.toString())) {
                            <span class="ng-value-icon right" (click)="clear(role)" aria-hidden="true">×</span>
                          }
                        </ng-template>
                      </ng-select>
                    </div>

                    <swal
                      #removePermissionsSwal
                      title="Remove User Branch Permissions?"
                      text="Are you sure you want to remove permissions for {{ selectedUser().name || selectedUser().email }}?"
                      icon="warning"
                      confirmButtonText="Yes, Remove"
                      [showCancelButton]="true"
                      [focusCancel]="true"
                      (confirm)="removeUserBranchPermissions(selectedUser(), item)"
                    >
                    </swal>

                    <app-pan-button color="danger" icon="delete" [swal]="removePermissionsSwal"></app-pan-button>
                  </div>
                </cdk-virtual-scroll-viewport>
              }
            }

            @case ("locations") {
              @if (visibleUserLocations().length === 0) {
                <app-pan-blank-slate>
                  There are no locations assigned to this user.

                  <app-pan-button icon="add" color="primary" fill="outline" (click)="addLocationModal.show()">Add one</app-pan-button>
                </app-pan-blank-slate>
              } @else {
                <cdk-virtual-scroll-viewport [itemSize]="50" class="h-[500px]">
                  <div *cdkVirtualFor="let item of visibleUserLocations()" class="h-[50px] p-2 flex items-center border-b-2 flex-row">
                    @let user = selectedUser();

                    <div class="flex-1 flex flex-col">
                      <div>{{ item.name }}</div>
                    </div>

                    <div class="flex-1 mr-2">
                      <ng-select
                        appendTo="body"
                        placeholder="Select roles..."
                        [bindValue]="'id'"
                        [bindLabel]="'name'"
                        [virtualScroll]="true"
                        [items]="item._selectableRoles"
                        [(ngModel)]="user.roles.location[item.id]"
                        [multiple]="true"
                        [clearable]="false"
                        (add)="addUserLocationRole(user, $event, item)"
                        (remove)="removeUserLocationRole(user, $event, item)"
                      >
                        <ng-template ng-label-tmp let-role="item" let-clear="clear">
                          <span class="ng-value-label">{{ role.name }}</span>

                          @if (userLogin.canGrantLocationPermission(role.id, item.branch_id.toString(), item.id.toString())) {
                            <span class="ng-value-icon right" (click)="clear(role)" aria-hidden="true">×</span>
                          }
                        </ng-template>
                      </ng-select>
                    </div>

                    <swal
                      #removePermissionsSwal
                      title="Remove User Location Permissions?"
                      text="Are you sure you want to remove permissions for {{ selectedUser().name || selectedUser().email }}?"
                      icon="warning"
                      confirmButtonText="Yes, Remove"
                      [showCancelButton]="true"
                      [focusCancel]="true"
                      (confirm)="removeUserLocationPermissions(selectedUser(), item)"
                    >
                    </swal>

                    <app-pan-button color="danger" icon="delete" [swal]="removePermissionsSwal"></app-pan-button>
                  </div>
                </cdk-virtual-scroll-viewport>
              }
            }
          }
        </div>
      }
    }
  </div>
</div>

<app-admin-user-management-create-user
  #newUserModal
  (createdUser)="addUserToList($event); newUserModal.hide()"
></app-admin-user-management-create-user>

<app-pan-modal #addBranchModal [topLevel]="false">
  <div header>Add User Branch</div>

  <div body class="flex flex-col justify-center items-center gap-2">
    <ng-select
      class="w-full"
      data-testid="modal-branch-roles-selector"
      placeholder="Select branch..."
      [items]="allBranches()"
      [bindLabel]="'name'"
      [virtualScroll]="true"
      [clearable]="false"
      [(ngModel)]="modalBranch"
      appendTo="body"
      [minTermLength]="2"
      typeToSearchText="Please enter 3 or more characters"
    >
    </ng-select>

    @if (modalBranch(); as chosenBranch) {
      <ng-select
        class="w-full"
        placeholder="Select roles..."
        [bindValue]="'id'"
        [bindLabel]="'name'"
        [virtualScroll]="true"
        [items]="chosenBranch._selectableRoles ?? []"
        [(ngModel)]="modalBranchRoles"
        [multiple]="true"
        [clearable]="false"
      >
        <ng-template ng-label-tmp let-item="item" let-clear="clear">
          <span class="ng-value-label">{{ item.name }}</span>

          @if (userLogin.canGrantBranchPermission(item.id, chosenBranch.id.toString())) {
            <span class="ng-value-icon right" (click)="clear(item)" aria-hidden="true">×</span>
          }
        </ng-template>
      </ng-select>
    }
  </div>

  <div footer>
    <button panButton panModalClose color="primary" (click)="addBranchToUser()" [disabled]="!canSubmitBranchModal()">
      Add Branch To User
    </button>
  </div>
</app-pan-modal>

<app-pan-modal #addLocationModal [topLevel]="false">
  <div header>Add User Location</div>

  <div body class="flex flex-col justify-center items-center gap-2">
    <ng-select
      class="w-full"
      data-testid="page-select-location-control"
      placeholder="Select location..."
      [items]="allLocations()"
      [bindLabel]="'name'"
      [virtualScroll]="true"
      [clearable]="false"
      [(ngModel)]="modalLocation"
      appendTo="body"
      [minTermLength]="2"
      [searchFn]="locationSearchFn"
      typeToSearchText="Please enter 3 or more characters"
    >
      <ng-template ng-label-tmp let-item="item">
        <span>
          {{ item.name }}
          @if (item.branch_name) {
            ({{ item.branch_name }})
          }
        </span>
      </ng-template>

      <ng-template ng-option-tmp let-item="item">
        <span>
          {{ item.name }}
          @if (item.branch_name) {
            ({{ item.branch_name }})
          }
        </span>
      </ng-template>
    </ng-select>

    @if (modalLocation(); as chosenLocation) {
      <ng-select
        class="w-full"
        data-testid="modal-location-roles-selector"
        placeholder="Select roles..."
        [bindValue]="'id'"
        [bindLabel]="'name'"
        [virtualScroll]="true"
        [items]="chosenLocation._selectableRoles ?? []"
        [(ngModel)]="modalLocationRoles"
        [multiple]="true"
        [clearable]="false"
      >
        <ng-template ng-label-tmp let-item="item" let-clear="clear">
          <span class="ng-value-label">{{ item.name }}</span>

          @if (userLogin.canGrantLocationPermission(item.id, chosenLocation.branch_id.toString(), chosenLocation.id.toString())) {
            <span class="ng-value-icon right" (click)="clear(item)" aria-hidden="true">×</span>
          }
        </ng-template>
      </ng-select>
    }
  </div>

  <div footer>
    <button panButton panModalClose color="primary" (click)="addLocationToUser()" [disabled]="!canSubmitLocationModal()">
      Add Location To User
    </button>
  </div>
</app-pan-modal>
